import { Chip } from "@components/atoms/Chip"
import { Button } from "@components/ui/Button"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import { SquadupEmbed } from "@features/ticketing/SquadUpEmbed"
import { useSquadup } from "@features/ticketing/useSquadUp"
import { useEventStatus } from "@hooks/useEventManifest"
import { formatNiceDate, formatNiceTime } from "@utils/date-format"
import { tv } from "@utils/tw"
import { EditableText } from "eddev/blocks"
import { Link } from "eddev/routing"
import { motion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { EventStatus, SessionTicketsFragment } from "../../types.graphql"
import { useAuth } from "@features/accounts/useAuth"
import { useRPCQuery } from "eddev/hooks"
import { SquadupOptions } from "@features/ticketing/squadup/squadup-options"
import dayjs from "dayjs"

export type Props = {
  session: SessionTicketsFragment
}

export function SessionTicketWidget(props: Props) {
  const flyingCarpet = useFlyingCarpet()
  const info = props.session.info

  const programType = props.session.film?.program?.programInfo?.programType

  const auth = useAuth()
  const accountLookup = useRPCQuery(
    "api.memberships.getAccount",
    { jwt: auth.session?.access_token! },
    {
      staleTime: 1,
      refetchInterval: 0,
      enabled: auth.loggedIn,
    },
  )
  const membership = accountLookup.data?.membership

  const isLoggedIn = auth.loggedIn
  const isMember = membership?.status === "active"
  const membershipRequirement = programType === "CineClub" || programType === "MembersOnly" ? "member" : "none"

  const squadupEventId = props.session.info?.ticketEventId
  const squadup = useSquadup()

  const realtimeStatus = useEventStatus(props.session?.id)

  const expander = useRef<HTMLDivElement>(null!)
  const [state, setState] = useState<"idle" | "loading" | "expanded">("idle")

  useEffect(() => {
    if (state === "loading") {
      const timeout = setTimeout(() => {
        const uri = new URL(window.location.href)
        uri.searchParams.set("opentickets", "1")
        document.location.href = uri.toString()
      }, 6000)
      return () => clearTimeout(timeout)
    }
  }, [state])

  const squadupEventOptions: Partial<SquadupOptions> = {
    eventId: squadupEventId!,
    showCheckout: false,
    shoppingCartEnabled: props.session.info?.disableShoppingCart === true ? false : true,
  }

  useEffect(() => {
    if (document.location.search.includes("opentickets") && squadupEventId) {
      squadup.open(squadupEventOptions)
      document.querySelector("#book-tickets-panel")?.scrollIntoView()
    }
  }, [])

  const styles = createStyles()

  const isPast = dayjs
    .tz(undefined, "America/New_York")
    .isAfter(dayjs.tz(props.session.info?.dateTime!, "America/New_York"))

  return (
    <div className={styles.base()}>
      <div className={styles.card()}>
        <div className={styles.info()}>
          <p className="flex gap-2">
            <span className="type-body-m-bold">{formatNiceDate(props.session.info?.dateTime!)}</span>
            <span className="type-body-m">{formatNiceTime(props.session.info?.dateTime!)}</span>
          </p>
          <p className="type-body-s flex items-center gap-2">
            {realtimeStatus?.status === "SoldOut" ? <Chip className="bg-red text-black">At Rush</Chip> : null}
            {realtimeStatus?.status === "SellingFast" ? (
              <Chip className="bg-white text-black">Selling Fast</Chip>
            ) : null}
            <Link
              href={info?.venue?.uri}
              className="hover-link flex items-center gap-[0.3em] leading-[0.5em] my-[0.5em]"
            >
              <span dangerouslySetInnerHTML={{ __html: info?.venue?.titleSimple! }}></span>
              {info?.venue?.titleSub && (
                <>
                  <span className="size-1 inline-block bg-current opacity-20" />
                  {info?.venue?.titleSub}
                </>
              )}
            </Link>
          </p>
        </div>
        <div className={styles.buttons()}>
          {info?.venue?.uri && (
            <Link href={info?.venue?.uri}>
              <Button
                size="icon"
                aria-label="See venue details"
                icon="location"
                className="bg-white/50 text-black hover:bg-white/80"
              />
            </Link>
          )}
          {isPast ? (
            <Button className="flex-1 md:justify-start" disabled leftIcon="ticket">
              Past Event
            </Button>
          ) : (
            <>
              {membershipRequirement === "member" && !isMember ? (
                <div className="flex-1 flex flex-col gap-1 items-stretch">
                  <Button className="flex-1 w-full md:justify-start" href="/membership/">
                    Become a Member
                  </Button>
                  {!isLoggedIn && (
                    <div className="type-body-xs text-right md:text-left">
                      Already a member?{" "}
                      <a onClick={() => flyingCarpet.launch("account")} className="cursor-pointer underline">
                        Log in
                      </a>
                    </div>
                  )}
                </div>
              ) : info?.externalUrl ? (
                <Button className="flex-1 md:justify-start" leftIcon="ticket" href={info?.externalUrl} target="_blank">
                  <EditableText as="span" store={"book_label_" + info.externalUrl} defaultValue="Book Tickets" />
                </Button>
              ) : (
                <Button
                  className="flex-1 md:justify-start"
                  disabled={!squadupEventId}
                  leftIcon="ticket"
                  onClick={() => {
                    console.log("squadupEventId", squadupEventId)
                    if (!squadupEventId) return
                    if (state !== "expanded") {
                      squadup.open(squadupEventOptions)
                    } else {
                      setState("idle")
                      squadup.close()
                    }
                  }}
                  loading={state === "loading"}
                >
                  {squadupEventId
                    ? realtimeStatus?.status === EventStatus.SoldOut
                      ? "At Rush"
                      : "Book Tickets"
                    : "Tickets Unavailable"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <motion.div
        className={styles.expander()}
        variants={{
          expanded: { height: "auto" },
          idle: { height: 0 },
          loading: { height: 0 },
        }}
        transition={{ ease: "easeOut" }}
        animate={state}
        ref={expander}
        onAnimationStart={(def) => {
          expander.current.style.overflow = "hidden"
          expander.current.style.display = "block"
        }}
        onAnimationComplete={(def) => {
          if (state === "expanded") {
            expander.current.style.overflow = "visible"
          } else {
            expander.current.style.display = "none"
          }
        }}
      >
        <SquadupEmbed
          className="embedded-event-tickets"
          enabled
          name="event"
          getPriority={(state) => {
            if (flyingCarpet.isOpen) return -1
            let priority = 0
            if (state.currentView === "eventDetail" || state.currentView === "seatingChart") {
              priority += 10
              if (state.viewingEvent?.id === squadupEventId) {
                priority += 5
              }
            }
            return priority
          }}
          onVisibleChange={(visible, loading) => setState(loading ? "loading" : visible ? "expanded" : "idle")}
        />
      </motion.div>
    </div>
  )
}

const createStyles = tv({
  slots: {
    base: ["flex flex-col"],
    card: ["flex flex-col bg-yellow gap-3 py-3 px-3", "md:flex-row md:items-center md:justify-between"],
    info: ["flex flex-col gap-0 pl-1"],
    buttons: "flex gap-3 flex-none",
    expander: ["border-2 border-yellow border-t-0 will-change-transform"],
  },
})
